import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import BranchSelect from "../../components/ui/BranchSelect";
import { useAttendanceList } from "../../api/queries/attendanceQueries";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import {
  formatPlainDate,
  formatTime,
  todayInManila,
} from "../../utils/dateTime";
import SessionStatusBadge from "./components/SessionStatusBadge";
import BetweenCheckIns from "../../components/ui/BetweenCheckIns";
import LocationsModal from "./components/LocationsModal";
import {
  AddDayDialog,
  DayStatusDialog,
  SessionDialog,
} from "./components/AttendanceEditDialogs";
import {
  DAY_STATUSES,
  DAY_STATUS_LABEL,
  SOURCE_LABEL,
  formatHours,
  sessionNotes,
} from "./components/attendanceFormat";
import { OvertimeTag } from "../dtr/components/DtrBits";
import "./attendance.css";
import "../dtr/dtr.css";
import { useCanModify } from "../../hooks/usePermissions";

const EMPTY = [];
const FILTER_KEYS = [
  "from",
  "to",
  "branchId",
  "search",
  "status",
  "sessionStatus",
  "outsideOnly",
  "assignmentType",
];

/** Sessions of one day, with per-session edit (HR) and status/reason. */
const SessionRows = ({ day, canEdit, onEditSession }) => (
  <table className="table table-sm mb-0 att-sessions">
    <thead>
      <tr>
        <th>#</th>
        <th>Check-in</th>
        <th>Check-out</th>
        <th
          className="text-end"
          title="Time of this check-in. It doesn't add up to the day: the day runs from first check-in to last check-out."
        >
          This check-in
        </th>
        <th>Status</th>
        <th>Source</th>
        <th>Notes</th>
        <th aria-label="Actions"></th>
      </tr>
    </thead>
    <tbody>
      {(day.sessions || []).map((s, i) => (
        <tr
          key={s.id || i}
          className={s.status === "REJECTED" ? "is-rejected" : ""}
        >
          <td>{i + 1}</td>
          <td>
            {formatTime(s.checkInTime)}
            {s.checkIn?.outsideBranch && (
              <i
                className="bi bi-geo-alt-fill text-danger ms-1"
                title="Outside the branch radius"
              ></i>
            )}
          </td>
          <td>
            {s.checkOutTime ? (
              formatTime(s.checkOutTime)
            ) : (
              <span className="text-warning-emphasis">Open</span>
            )}
            {s.checkOut?.outsideBranch && (
              <i
                className="bi bi-geo-alt-fill text-danger ms-1"
                title="Outside the branch radius"
              ></i>
            )}
          </td>
          <td className="text-end tabular-nums text-slate-500">
            {formatHours(s.hours)}
          </td>
          <td>
            <SessionStatusBadge status={s.status} reason={s.statusReason} />
            {s.statusReason && (
              <div className="fs-070 text-slate-500">{s.statusReason}</div>
            )}
            {s.reviewRemarks && (
              <div className="fs-070 text-slate-500 fst-italic">
                “{s.reviewRemarks}”
              </div>
            )}
          </td>
          <td className="fs-075">
            {SOURCE_LABEL[s.source] || s.source || "—"}
            {s.editedAt && (
              <div className="fs-070 text-slate-500">edited by HR</div>
            )}
          </td>
          <td className="fs-075 text-slate-600">
            {s.clientName && (
              <div>
                <i className="bi bi-briefcase me-1"></i>
                {s.clientName}
              </div>
            )}
            {sessionNotes(s) || (s.clientName ? "" : "—")}
          </td>
          <td className="text-end">
            {canEdit &&
              (s.id ? (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() => onEditSession(s)}
                >
                  Edit
                </button>
              ) : (
                <span
                  className="fs-070 text-slate-400"
                  title="Old record: set the day's status or add a period to change it"
                >
                  old record
                </span>
              ))}
          </td>
        </tr>
      ))}
      {(day.sessions || []).length === 0 && (
        <tr>
          <td colSpan={8} className="text-slate-500 fs-080">
            No check-ins.
          </td>
        </tr>
      )}
    </tbody>
  </table>
);

/**
 * HR / managers: attendance days with their check-in periods, locations and
 * HR edits. Filters live in the URL (dates default to today).
 * embedded: inside the Time module, which shows search / branch / type in its
 * shared filter bar (same URL params), so they're not repeated here.
 */
export const AttendanceRecords = ({ embedded = false }) => {
  const user = useCurrentUser();
  // Configured roles need Attendance: modify to add / edit days (unconfigured: as before)
  const canModify = useCanModify("attendance");
  const [params, setParams] = useSearchParams();
  const today = todayInManila();
  const page = Number(params.get("page")) || 1;
  const limit = Number(params.get("limit")) || 20;
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])),
    [params],
  );
  // Default range: today only (pick other dates in the filters)
  const from = filters.from || today;
  const to = filters.to || today;

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k),
    );
    if (resetPage && !("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const [searchText, setSearchText] = useState(filters.search);
  useEffect(() => {
    const value = searchText.trim();
    if (value === filters.search) return undefined;
    const t = setTimeout(() => update({ search: value }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const query = useAttendanceList({
    from,
    to,
    branchId: filters.branchId,
    search: filters.search,
    status: filters.status,
    sessionStatus: filters.sessionStatus,
    outsideOnly: filters.outsideOnly === "true",
    assignmentType: filters.assignmentType,
    companyId: user.companyId || undefined,
    page,
    limit,
  });
  const days = query.data?.data || EMPTY;
  const total = query.data?.total || 0;

  const [expanded, setExpanded] = useState(() => new Set());
  const [locationsFor, setLocationsFor] = useState(null);
  const [dialog, setDialog] = useState(null); // { type: 'session'|'status'|'addDay', day?, session? }

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // HR edits: any employee in scope (deployed ones too), not your own record (the backend checks too)
  const canEditDay = (day) =>
    canModify &&
    day.employee?.id !== user.employeeId &&
    day.employeeId !== user.employeeId;
  const hasFilters = FILTER_KEYS.some((k) => filters[k]);

  return (
    <>
      <div>
        <div
          className={`d-flex flex-wrap ${embedded ? "justify-content-end" : "justify-content-between"} align-items-center gap-2 ${embedded ? "mb-2" : "mb-4"}`}
        >
          {!embedded && (
            <div>
              <h1 className="fw-bold text-dark m-0 page-title">Attendance</h1>
              <p className="text-muted small mb-0">
                Check-ins per day, their validation and locations. Times are
                server (Manila) time.
              </p>
            </div>
          )}
          <div className="d-flex flex-wrap gap-2">
            <Link
              to="/approvals?tab=validation"
              className="btn profile-btn profile-btn-white"
            >
              <i className="bi bi-patch-check"></i> Check-ins to validate
            </Link>
            {canModify && (
              <button
                type="button"
                className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
                onClick={() => setDialog({ type: "addDay" })}
              >
                <i className="bi bi-plus-lg me-2"></i> Add missing day
              </button>
            )}
          </div>
        </div>

        <div className="att-filters mb-3">
          <div className="d-flex align-items-center gap-1">
            <input
              type="date"
              className="filter-input"
              value={from}
              max={to}
              onChange={(e) =>
                update({
                  from: e.target.value === today ? "" : e.target.value,
                })
              }
              aria-label="From"
            />
            <span className="text-slate-400">–</span>
            <input
              type="date"
              className="filter-input"
              value={to}
              min={from}
              onChange={(e) =>
                update({ to: e.target.value === today ? "" : e.target.value })
              }
              aria-label="To"
            />
          </div>
          {!embedded && (
            <div className="position-relative att-filter-search">
              <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
              <input
                type="search"
                className="filter-input w-100 ps-5"
                placeholder="Name or employee no."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                aria-label="Search"
              />
            </div>
          )}
          {!embedded && (
            <div className="att-filter-branch">
              <BranchSelect
                value={filters.branchId}
                onChange={(v) => update({ branchId: v })}
                id="att-branch"
              />
            </div>
          )}
          <select
            className="status-select"
            value={filters.status}
            onChange={(e) => update({ status: e.target.value })}
            aria-label="Day status"
          >
            <option value="">All statuses</option>
            {DAY_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {!embedded && (
            <select
              className="status-select"
              value={filters.assignmentType}
              onChange={(e) => update({ assignmentType: e.target.value })}
              aria-label="Employee type"
            >
              <option value="">Internal and deployed</option>
              <option value="Internal">Internal</option>
              <option value="Deployed">Deployed</option>
            </select>
          )}
          <div className="form-check form-switch m-0">
            <input
              id="att-review"
              type="checkbox"
              className="form-check-input"
              checked={filters.sessionStatus === "FOR_REVIEW"}
              onChange={(e) =>
                update({ sessionStatus: e.target.checked ? "FOR_REVIEW" : "" })
              }
            />
            <label htmlFor="att-review" className="form-check-label fs-080">
              Needs validation
            </label>
          </div>
          <div className="form-check form-switch m-0">
            <input
              id="att-outside"
              type="checkbox"
              className="form-check-input"
              checked={filters.outsideOnly === "true"}
              onChange={(e) =>
                update({ outsideOnly: e.target.checked ? "true" : "" })
              }
            />
            <label htmlFor="att-outside" className="form-check-label fs-080">
              Outside branch only
            </label>
          </div>
          {hasFilters && (
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600"
              onClick={() => {
                setSearchText("");
                update(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])));
              }}
            >
              Clear
            </button>
          )}
          {query.isFetching && !query.isLoading && (
            <span
              className="spinner-border spinner-border-sm text-primary ms-auto"
              role="status"
              aria-label="Refreshing"
            ></span>
          )}
        </div>

        <div className="glass-table-card">
          {query.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(query.error, "Could not load attendance.")}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table mb-0 att-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th
                        className="ps-4 py-3 att-col-expand"
                        aria-label="Expand"
                      ></th>
                      <th className="py-3">Date</th>
                      <th className="py-3">Employee</th>
                      <th className="py-3">First in · Last out</th>
                      <th className="py-3 text-end">Hours</th>
                      <th className="py-3">Status</th>
                      <th className="py-3 text-center">Periods</th>
                      <th className="py-3">Flags</th>
                      <th className="py-3 pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {days.map((d) => {
                      const isOpen = expanded.has(d.id);
                      const editable = canEditDay(d);
                      return (
                        <React.Fragment key={d.id}>
                          <tr className={isOpen ? "att-row-open" : ""}>
                            <td className="ps-4">
                              <button
                                type="button"
                                className="btn btn-sm btn-light border btn-icon-28"
                                onClick={() => toggle(d.id)}
                                aria-expanded={isOpen}
                                aria-label={`${isOpen ? "Hide" : "Show"} check-in periods`}
                              >
                                <i
                                  className={`bi bi-chevron-${isOpen ? "down" : "right"}`}
                                ></i>
                              </button>
                            </td>
                            <td className="text-nowrap fw-semibold">
                              {formatPlainDate(d.date, "ddd, MMM D")}
                            </td>
                            <td>
                              <div className="emp-name-text">
                                {d.employee?.name || "—"}
                              </div>
                              <div className="emp-sub-text">
                                {d.employee?.employeeNumber}
                                {d.employee?.branchName
                                  ? ` · ${d.employee.branchName}`
                                  : ""}
                              </div>
                            </td>
                            <td className="text-nowrap">
                              {d.checkInTime ? formatTime(d.checkInTime) : "—"}{" "}
                              ·{" "}
                              {d.checkOutTime ? (
                                formatTime(d.checkOutTime)
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="text-end tabular-nums">
                              {formatHours(d.workHours)}
                            </td>
                            <td>
                              <span className="att-day-status">
                                {DAY_STATUS_LABEL[d.status] || d.status || "—"}
                              </span>
                              {d.isLate && (
                                <span className="day-chip day-chip--late">
                                  Late
                                </span>
                              )}
                            </td>
                            <td className="text-center tabular-nums">
                              {d.sessionCount ?? (d.sessions || []).length}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                <OvertimeTag day={d} />
                                {d.forReviewCount > 0 && (
                                  <span className="status-pill status-pill--amber">
                                    {d.forReviewCount} for review
                                  </span>
                                )}
                                {d.outsideBranch && (
                                  <span className="status-pill status-pill--red">
                                    Outside branch
                                  </span>
                                )}
                                {(d.source === "BIOMETRIC" ||
                                  d.source === "MANUAL") && (
                                  <span className="chip chip--sm">
                                    {SOURCE_LABEL[d.source]}
                                  </span>
                                )}
                                {d.isCorrected && (
                                  <span className="chip chip--sm chip--alt">
                                    Edited by HR
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="pe-4 text-end text-nowrap">
                              <div className="d-inline-flex gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light border att-action-btn"
                                  onClick={() => setLocationsFor(d.id)}
                                  disabled={!d.hasLocation}
                                  title={
                                    d.hasLocation
                                      ? "View check-in locations"
                                      : "No location recorded"
                                  }
                                >
                                  <i className="bi bi-geo-alt"></i> Locations
                                </button>
                                {editable && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light border att-action-btn"
                                      onClick={() =>
                                        setDialog({ type: "status", day: d })
                                      }
                                      title="Set status / remarks"
                                      aria-label="Set status / remarks"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light border att-action-btn"
                                      onClick={() =>
                                        setDialog({ type: "session", day: d })
                                      }
                                      title="Add check-in period"
                                      aria-label="Add check-in period"
                                    >
                                      <i className="bi bi-plus-lg"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="att-sessions-row">
                              <td></td>
                              <td colSpan={8} className="pe-4">
                                <SessionRows
                                  day={d}
                                  canEdit={editable}
                                  onEditSession={(s) =>
                                    setDialog({
                                      type: "session",
                                      day: d,
                                      session: s,
                                    })
                                  }
                                />
                                <BetweenCheckIns gaps={d.gaps} />
                                {!editable && (
                                  <div className="fs-070 text-slate-500 mt-1">
                                    Read-only:{" "}
                                    {!canModify
                                      ? "view-only access"
                                      : "your own record"}
                                    .
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {!query.isLoading && days.length === 0 && (
                      <tr>
                        <td colSpan={9} className="approval-empty">
                          <i className="bi bi-calendar-x"></i>
                          No attendance for these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {query.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <Pagination
                page={page}
                limit={limit}
                total={total}
                onPageChange={(p) =>
                  update({ page: String(p) }, { resetPage: false })
                }
                onLimitChange={(l) => update({ limit: String(l) })}
              />
            </>
          )}
        </div>
      </div>

      {locationsFor && (
        <LocationsModal
          attendanceId={locationsFor}
          onClose={() => setLocationsFor(null)}
        />
      )}
      {dialog?.type === "session" && (
        <SessionDialog
          day={dialog.day}
          session={dialog.session}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === "status" && (
        <DayStatusDialog day={dialog.day} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "addDay" && (
        <AddDayDialog onClose={() => setDialog(null)} />
      )}
    </>
  );
};

/** /attendance: the records as their own page (also a tab of the Time module). */
const AttendancePage = () => (
  <Layout>
    <div className="container-fluid py-4 px-lg-4">
      <AttendanceRecords />
    </div>
  </Layout>
);

export default AttendancePage;
